import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useConsultationForm } from '../hooks/useConsultationForm';
import { isModuleEnabled } from '../../../config/modules';
import { appointmentService } from '../../../services/appointmentService';
import { labTestService, type LabTest, type LabPanel } from '../../../services/labTestService';
import { imagingStudyService, type ImagingStudy } from '../../../services/imagingStudyService';
import { medicationService, type Medication } from '../../../services/pharmacyService';
import { tnmStagingService } from '../../../services/tnmStagingService';
import PediatricSection from '../components/pediatric/PediatricSection';
import InternalMedicineSection from '../components/internalMedicine/InternalMedicineSection';
import TraumatologiaSection from '../components/traumatologia/TraumatologiaSection';
import DermatologiaSection from '../components/dermatologia/DermatologiaSection';
import OftalmologiaSection from '../components/oftalmologia/OftalmologiaSection';
import SOAPFields from '../components/SOAPFields';
import ReviewOfSystemsChecklist from '../components/ReviewOfSystemsChecklist';
import TnmStagingForm from '../components/TnmStagingForm';
import type { TnmStaging, TnmStagingInput } from '../types/consultation.types';
import Spinner from '../../../components/ui/Spinner';
import ErrorBoundary from '../../../components/common/ErrorBoundary';
import ConfirmModal from '../../../components/common/ConfirmModal';
import Combobox from '../../../components/ui/Combobox';
import type { Diagnosis, Appointment } from '../../../types/api';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import SectionNumberBadge from '../components/SectionNumberBadge';
import SectionJumpNav from '../components/SectionJumpNav';
import { useCanSign } from '../hooks/useConsultationAccess';

const LabTestSelect: React.FC<{
  value: number | null;
  panelId: number | null;
  onSelectTest: (id: number, name: string) => void;
  onSelectPanel: (id: number, name: string) => void;
  onClear: () => void;
}> = ({ value, panelId, onSelectTest, onSelectPanel, onClear }) => {
  const [panels, setPanels] = useState<LabPanel[]>([]);

  const displayName = () => {
    if (panelId) {
      const panel = panels.find(p => p.id === panelId);
      return panel ? `${panel.code} — ${panel.name}` : '';
    }
    return '';
  };

  return (
    <Combobox<LabTest>
      hasSelection={!!(value || panelId)}
      displayValue={displayName()}
      placeholder="Buscar prueba o seleccionar panel..."
      onOpen={() => {
        labTestService.getAll().then(setPanels).catch((e) => console.error('Error loading lab panels:', e));
      }}
      fetchResults={(q) => labTestService.search(q)}
      onSelect={(item) => onSelectTest(item.id, item.name)}
      onClear={onClear}
      getItemKey={(t) => t.id}
      renderResult={(t) => (
        <>
          <strong>{t.code}</strong> — {t.name}
          {t.unit && <span className="text-muted ms-2">({t.unit})</span>}
        </>
      )}
      renderBelowResults={(close) => panels.length > 0 && (
        <>
          <div className="list-group-item list-group-item-secondary fw-bold small mt-1">PANELES</div>
          {panels.map((p) => (
            <div
              key={p.id}
              tabIndex={0}
              role="option"
              className="list-group-item list-group-item-action"
              style={{ cursor: 'pointer' }}
              onClick={() => { onSelectPanel(p.id, p.name); close(); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectPanel(p.id, p.name); close(); } }}
            >
              <strong>{p.code}</strong> — {p.name}
            </div>
          ))}
        </>
      )}
    />
  );
};

// ==================== Imaging Study Select Component ====================
const ImagingStudySelect: React.FC<{
  value: number | null;
  onSelect: (id: number, name: string) => void;
  onClear: () => void;
}> = ({ value, onSelect, onClear }) => {
  const [studyName, setStudyName] = useState('');

  useEffect(() => {
    if (value && !studyName) {
      imagingStudyService.getById(value).then(s => setStudyName(s.name)).catch((e) => console.error('Error loading imaging study:', e));
    }
  }, [value, studyName]);

  return (
    <Combobox<ImagingStudy>
      hasSelection={!!value}
      displayValue={studyName}
      placeholder="Buscar estudio de imagen..."
      readOnlyWhenSelected={false}
      fetchResults={(q) => imagingStudyService.search(q)}
      onQueryChange={() => setStudyName('')}
      onSelect={(item) => { onSelect(item.id, item.name); setStudyName(item.name); }}
      onClear={() => { onClear(); setStudyName(''); }}
      getItemKey={(s) => s.id}
      renderResult={(s) => (
        <>
          <strong>{s.code}</strong> — {s.name}
          {s.requires_contrast && <span className="badge bg-warning ms-2">Contraste</span>}
          {s.department && <span className="text-muted ms-2 small">({s.department.name})</span>}
        </>
      )}
    />
  );
};

// ==================== Medication Select Component ====================
const MedicationSelect: React.FC<{
  value: number | null;
  onSelect: (id: number, name: string) => void;
  onClear: () => void;
}> = ({ value, onSelect, onClear }) => {
  const [medName, setMedName] = useState('');

  useEffect(() => {
    if (value && !medName) {
      medicationService.getById(value).then(m => setMedName(m.name)).catch((e) => console.error('Error loading medication:', e));
    }
  }, [value, medName]);

  return (
    <Combobox<Medication>
      hasSelection={!!value}
      displayValue={medName}
      placeholder="Buscar medicamento..."
      fetchResults={(q) => medicationService.getAll({ search: q, per_page: 30, is_active: true }).then(res => res.data)}
      onSelect={(item) => { onSelect(item.id, item.name); setMedName(item.name); }}
      onClear={() => { onClear(); setMedName(''); }}
      getItemKey={(m) => m.id}
      renderResult={(m) => (
        <>
          <strong>{m.name}</strong>
          {m.active_ingredient && <span className="text-muted ms-2 small">({m.active_ingredient})</span>}
        </>
      )}
    />
  );
};

// Clinical safety ranges for vital signs. Out-of-range values are flagged
// inline (is-invalid) but DO NOT block submit — warning pattern, not a gate.
type VitalField = 'heart_rate' | 'respiratory_rate' | 'systolic_pressure' | 'diastolic_pressure' | 'temperature' | 'weight' | 'height' | 'oxygen_saturation';

interface VitalRange {
  min: number;
  max: number;
  label: string;
  unit: string;
}

const VITAL_RANGES: Record<VitalField, VitalRange> = {
  heart_rate: { min: 20, max: 300, label: 'FC', unit: 'lpm' },
  respiratory_rate: { min: 0, max: 80, label: 'FR', unit: 'rpm' },
  systolic_pressure: { min: 30, max: 300, label: 'PA Sistólica', unit: 'mmHg' },
  diastolic_pressure: { min: 10, max: 200, label: 'PA Diastólica', unit: 'mmHg' },
  temperature: { min: 30, max: 45, label: 'Temperatura', unit: '°C' },
  weight: { min: 0.5, max: 500, label: 'Peso', unit: 'kg' },
  height: { min: 20, max: 250, label: 'Talla', unit: 'cm' },
  oxygen_saturation: { min: 0, max: 100, label: 'SpO2', unit: '%' },
};

const getVitalRangeStatus = (
  field: VitalField,
  raw: string,
): { outOfRange: boolean; message: string | null } => {
  if (!raw) return { outOfRange: false, message: null };
  const num = parseFloat(raw);
  if (isNaN(num)) return { outOfRange: false, message: null };
  const range = VITAL_RANGES[field];
  if (num < range.min || num > range.max) {
    return {
      outOfRange: true,
      message: `${range.label} fuera de rango (${range.min}–${range.max} ${range.unit})`,
    };
  }
  return { outOfRange: false, message: null };
};

const ConsultationFormPage: React.FC = () => {
  const { id: editIdParam } = useParams<{ id?: string }>();
  const editId = editIdParam ? Number(editIdParam) : undefined;
  const form = useConsultationForm(editId);
  const navigate = useNavigate();
  // "Firmar consulta" from the edit page after finalizing (task 3.4). Built
  // from the fields useConsultationForm already tracks, rather than a full
  // MedicalConsultation, since that is all this page loads.
  const signableConsultation = editId && form.doctorId !== null
    ? { id: editId, doctor_id: form.doctorId, status: form.consultationStatus ?? undefined, lock_version: form.consultationLockVersion ?? undefined, created_by: form.consultationCreatedBy ?? undefined }
    : null;
  // Doctors the assistant may sign for right now (R12), from the my-doctors
  // list this page already loads.
  const delegatedSignerDoctorIds = useMemo(
    () => form.myDoctors.filter((d) => d.can_sign_delegated).map((d) => d.id),
    [form.myDoctors],
  );
  const { canSign, signing, error: signError, sign } = useCanSign(signableConsultation as any, () => navigate(`/consultations/${editId}`), delegatedSignerDoctorIds);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const patientSex = form.patientGender === null ? null : form.patientGender ? 'male' : 'female';
  const patientAge = form.patientBirthDate
    ? Math.round((new Date().getTime() - new Date(form.patientBirthDate).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
    : null;
  const [searchParams] = useSearchParams();
  const [patientSearch, setPatientSearch] = useState('');
  const [diagnosisSearch, setDiagnosisSearch] = useState('');
  const [diagnosisType, setDiagnosisType] = useState('principal');
  const [linkedAppointment, setLinkedAppointment] = useState<Appointment | null>(null);
  const [loadingAppointment, setLoadingAppointment] = useState(false);
  const patientSearchTimeout = useRef<ReturnType<typeof setTimeout>>();
  const diagnosisSearchTimeout = useRef<ReturnType<typeof setTimeout>>();

  // SOAP fields state (from hook — wired to buildSharedPayload)
  // Review of Systems state is owned by useConsultationForm (initialized to all 14 systems
  // in the hook) and persisted via PATCH /consultations/{id}/review-of-systems on submit.

  // TNM Staging state
  const [isOncologyPatient, setIsOncologyPatient] = useState(false);
  const [showTnmStaging, setShowTnmStaging] = useState(false);
  const [tnmStaging, setTnmStaging] = useState<TnmStaging | null>(null);
  const [loadingTnmStaging, setLoadingTnmStaging] = useState(false);

  // Inline validation state
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Submit state machine — guards against re-submission while the success
  // alert is visible and before the redirect timer fires.
  type SubmitState = 'idle' | 'submitting' | 'success' | 'error';
  const [submitState, setSubmitState] = useState<SubmitState>('idle');

  useEffect(() => {
    if (submitState !== 'submitting') return;
    if (form.success) {
      setSubmitState('success');
    } else if (form.error) {
      setSubmitState('error');
    } else if (!form.loading) {
      setSubmitState('idle');
    }
  }, [submitState, form.loading, form.success, form.error]);

  useEffect(() => {
    if (submitState === 'error' && !form.error) {
      setSubmitState('idle');
    }
  }, [submitState, form.error]);

  // Validation helper
  const validateField = (name: string, value: unknown): string | null => {
    switch (name) {
      case 'patientId':
        if (!form.patientId) return 'Debe seleccionar un paciente';
        return null;
      case 'doctorId':
        if (!form.doctorId) return 'Debe seleccionar un doctor';
        return null;
      case 'chiefComplaint':
        if (!value || (typeof value === 'string' && value.trim().length === 0)) {
          return 'El motivo de consulta es requerido';
        }
        if (typeof value === 'string' && value.trim().length < 3) {
          return 'El motivo de consulta debe tener al menos 3 caracteres';
        }
        return null;
      case 'currentIllness':
        if (!value || (typeof value === 'string' && value.trim().length === 0)) {
          return 'La enfermedad actual es requerida';
        }
        return null;
      default:
        return null;
    }
  };

  // Validate all required fields and scroll to first error
  const validateAllFields = (): boolean => {
    const errors: Record<string, string> = {};

    const patientError = validateField('patientId', form.patientId);
    if (patientError) errors.patientId = patientError;

    const doctorError = validateField('doctorId', form.doctorId);
    if (doctorError) errors.doctorId = doctorError;

    const chiefError = validateField('chiefComplaint', form.chiefComplaint);
    if (chiefError) errors.chiefComplaint = chiefError;

    const illnessError = validateField('currentIllness', form.currentIllness);
    if (illnessError) errors.currentIllness = illnessError;

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      // Scroll to first error field
      const firstErrorKey = Object.keys(errors)[0];
      const element = document.querySelector(`[data-field="${firstErrorKey}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return false;
    }
    return true;
  };

  // Handle submit with validation
  const handleSubmit = () => {
    if (submitState === 'submitting' || submitState === 'success') {
      return;
    }
    if (validateAllFields()) {
      setSubmitState('submitting');
      form.submit();
    }
  };

  const dismissSubmitError = () => {
    setSubmitState('idle');
  };

  // Cancel navigation: in new mode with unsaved changes, prompt before
  // discarding the in-progress draft. In edit mode (or when the form
  // is clean), navigate immediately — no modal noise.
  const handleCancelClick = (e: React.MouseEvent) => {
    if (!form.isEditMode && form.hasUnsavedChanges) {
      e.preventDefault();
      setShowCancelConfirm(true);
    }
  };

  const handleConfirmDiscard = () => {
    localStorage.removeItem('consultation_draft_new');
    setShowCancelConfirm(false);
    navigate('/consultations');
  };

  useEffect(() => {
    const init = async () => {
      await form.fetchDoctors();

      if (editId) {
        const c = await form.loadConsultation(editId);
        if (c?.patient) {
          setPatientSearch(`${c.patient.first_name} ${c.patient.last_name} - ${c.patient.identification || ''}`);
          setIsOncologyPatient(c.patient.is_oncology ?? false);
          // Load existing TNM staging for edit mode
          if (editId) {
            setLoadingTnmStaging(true);
            try {
              const staging = await tnmStagingService.get(editId);
              setTnmStaging(staging);
              if (staging) setShowTnmStaging(true);
            } catch (e) { console.error('Error loading TNM staging:', e); }
            setLoadingTnmStaging(false);
          }
        }
        return;
      }

      const apptId = searchParams.get('appointment_id');
      if (apptId) {
        setLoadingAppointment(true);
        try {
          const appt = await appointmentService.getById(Number(apptId));
          setLinkedAppointment(appt);
          form.setAppointmentId(appt.id);
          form.setPatientId(appt.patient_id);
          form.setDoctorId(appt.doctor_id);
          if (appt.patient) {
            setPatientSearch(`${appt.patient.first_name} ${appt.patient.last_name} - ${appt.patient.identification}`);
            setIsOncologyPatient(appt.patient.is_oncology ?? false);
          }
          if (appt.reason) {
            form.setChiefComplaint(appt.reason);
          }
        } catch (e) { console.error('Error loading appointment:', e); }
        setLoadingAppointment(false);
        return;
      }

      const patientId = searchParams.get('patient_id');
      if (patientId) {
        try {
          const { patientService } = await import('../../../services/patientService');
          const patient = await patientService.getById(Number(patientId));
          form.setPatientId(patient.id);
          setPatientSearch(`${patient.first_name} ${patient.last_name} - ${patient.identification || ''}`);
          setIsOncologyPatient(patient.is_oncology ?? false);
        } catch (e) { console.error('Error loading patient from URL:', e); }
      }
    };
    init();
  }, []);

  // Edit mode: show the loaded consultation's patient as soon as it is known.
  useEffect(() => {
    if (form.loadedPatientLabel) setPatientSearch(form.loadedPatientLabel);
  }, [form.loadedPatientLabel]);

  const handlePatientSearch = (value: string) => {
    setPatientSearch(value);
    clearTimeout(patientSearchTimeout.current);
    patientSearchTimeout.current = setTimeout(() => {
      form.searchPatients(value);
    }, 300);
  };

  const handleDiagnosisSearch = (value: string) => {
    setDiagnosisSearch(value);
    clearTimeout(diagnosisSearchTimeout.current);
    diagnosisSearchTimeout.current = setTimeout(() => {
      form.searchDiagnoses(value);
    }, 300);
  };

  const handleAddDiagnosis = (diagnosis: Diagnosis) => {
    form.addDiagnosis(diagnosis, diagnosisType);
    setDiagnosisSearch('');
  };

  // TNM Staging handlers
  const handleSaveTnmStaging = async (data: TnmStagingInput) => {
    if (!editId) {
      throw new Error('Consultation must be saved first');
    }
    const staging = await tnmStagingService.upsert(editId, data);
    setTnmStaging(staging);
  };

  const handleDeleteTnmStaging = async () => {
    if (!editId) return;
    await tnmStagingService.delete(editId);
    setTnmStaging(null);
    setShowTnmStaging(false);
  };

  const specialtyLower = form.doctorSpecialty?.toLowerCase() ?? '';
  const isPediatricSpecialty = specialtyLower.includes('pediatr');
  const isInternalMedicineSpecialty = ['medicina interna', 'interna', 'm. interna', 'internal medicine'].some(s => specialtyLower.includes(s));
  const isTraumatologiaSpecialty = ['traumatolog', 'ortopedia'].some(s => specialtyLower.includes(s));
  const isDermatologiaSpecialty = ['dermatolog', 'dermato'].some(s => specialtyLower.includes(s));
  const isOftalmologiaSpecialty = ['oftalmolog', 'oftalmo'].some(s => specialtyLower.includes(s));
  const isCardiologiaSpecialty = specialtyLower.includes('cardiolog');
  const isGinecologiaSpecialty = specialtyLower.includes('ginec');
  const isCirugiaSpecialty = specialtyLower.includes('ciru');
  const showTnmSection = form.isEditMode && !!editId && (isOncologyPatient || showTnmStaging || !!tnmStaging);

  const sectionDefs: { key: string; label: string; visible: boolean }[] = [
    { key: 'paciente-doctor', label: 'Paciente / Doctor', visible: true },
    { key: 'datos-consulta', label: 'Datos de Consulta', visible: true },
    { key: 'signos-vitales', label: 'Signos Vitales', visible: true },
    { key: 'pediatrica', label: 'Pediatría', visible: isPediatricSpecialty },
    { key: 'medicina-interna', label: 'Medicina Interna', visible: isInternalMedicineSpecialty },
    { key: 'traumatologica', label: 'Traumatología', visible: isTraumatologiaSpecialty },
    { key: 'dermatologica', label: 'Dermatología', visible: isDermatologiaSpecialty },
    { key: 'oftalmologica', label: 'Oftalmología', visible: isOftalmologiaSpecialty },
    { key: 'diagnosticos', label: 'Diagnósticos', visible: true },
    { key: 'recetas', label: 'Recetas', visible: true },
    { key: 'lab', label: 'Laboratorio', visible: true },
    { key: 'imagenes', label: 'Imágenes', visible: true },
    { key: 'info-adicional', label: 'Info. Adicional', visible: true },
    { key: 'cardiologica', label: 'Cardiología', visible: isCardiologiaSpecialty },
    { key: 'ginecologica', label: 'Ginecología', visible: isGinecologiaSpecialty },
    { key: 'quirurgica', label: 'Cirugía', visible: isCirugiaSpecialty },
    { key: 'soap', label: 'SOAP', visible: true },
    { key: 'ros', label: 'Rev. por Sistemas', visible: true },
    { key: 'tnm', label: 'TNM', visible: showTnmSection },
  ];

  const sectionNumbers: Record<string, string> = {};
  let sectionCount = 0;
  sectionDefs.forEach((s) => {
    if (!s.visible) return;
    sectionCount += 1;
    sectionNumbers[s.key] = String(sectionCount).padStart(2, '0');
  });

  const jumpNavSections = sectionDefs
    .filter((s) => s.visible)
    .map((s) => ({ key: s.key, label: s.label, number: sectionNumbers[s.key] }));

  // Full-page loading in edit mode while consultation data is being fetched
  if (form.isEditMode && form.loadingEdit) {
    return (
      <>
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col-sm-6">
              <Breadcrumb items={[{ label: 'Consultas', to: '/consultations' }, { label: 'Editar Consulta' }]} />
            </div>
            <div className="col-sm-6 text-end">
              <Link
                to="/consultations"
                className="btn btn-outline-secondary"
                onClick={(e) => {
                  // ConfirmModal handles the "dirty form" case.
                  // The page-level handler reads form.hasUnsavedChanges
                  // and form.isEditMode; here we let the Link's default
                  // behavior handle clean forms and edit mode.
                  if (!form.isEditMode && form.hasUnsavedChanges) {
                    e.preventDefault();
                    setShowCancelConfirm(true);
                  }
                }}
              >
                <i className="fa fa-arrow-left me-1"></i> Volver
              </Link>
            </div>
          </div>
        </div>
        <Spinner variant="full-page" text="Cargando consulta..." />
      </>
    );
  }

  // clinical-delegation R3 / verify-report WARNING 1: build the doctor
  // selector's option list explicitly, in JS, rather than nested JSX
  // branches — this is what keeps the "always include the loaded record's
  // doctor" fallback (Part B of the review) a single, testable rule
  // instead of a duplicated check in each branch.
  type DoctorOption = { id: number; label: string };
  const doctorOptions: DoctorOption[] = [];
  if (form.myDoctors.length > 0) {
    // An "Asistente Médico" may only choose among doctors they are
    // actively assigned to (clinical-delegation R3) — task 3.1/3.2.
    form.myDoctors.forEach((d) => {
      doctorOptions.push({ id: d.id, label: `Dr. ${d.full_name}${d.specialty ? ` — ${d.specialty}` : ''}` });
    });
  } else {
    if (form.currentDoctor && !form.doctors.some((d) => d.id === form.currentDoctor!.id)) {
      doctorOptions.push({
        id: form.currentDoctor.id,
        label: `Dr. ${form.currentDoctor.first_name} ${form.currentDoctor.last_name}${form.currentDoctor.specialty ? ` — ${form.currentDoctor.specialty.name}` : ''}`,
      });
    }
    form.doctors.forEach((d) => {
      doctorOptions.push({ id: d.id, label: `Dr. ${d.first_name} ${d.last_name}${d.specialty ? ` — ${d.specialty.name}` : ''}` });
    });
  }
  // Edit mode: the consultation's actual doctor, from the loaded record
  // itself (not user.doctor) — so an assistant editing a consultation for a
  // doctor whose assignment was since revoked, or anyone editing a record
  // whose doctor fell outside the rendered list, never sees a blank select.
  if (form.loadedDoctor && !doctorOptions.some((o) => o.id === form.loadedDoctor!.id)) {
    doctorOptions.push({
      id: form.loadedDoctor.id,
      label: `Dr. ${form.loadedDoctor.first_name} ${form.loadedDoctor.last_name}${form.loadedDoctor.specialty ? ` — ${form.loadedDoctor.specialty.name}` : ''}`,
    });
  }
  // clinical-delegation R3/R6, verify-report WARNING 1: a confirmed
  // "Asistente Médico" with zero active assignments gets an inert,
  // disabled selector with a clear message instead of silently falling
  // back to the unrestricted full doctor list (which the backend would
  // 403 anyway, but only after the assistant fills out the whole form).
  const isInertAssistantSelector = form.isAssistant && form.myDoctors.length === 0 && !form.loadedDoctor;

  return (
    <>
      {/* Page Header */}
      <div className="page-header">
        <div className="row align-items-center">
          <div className="col-sm-6">
            <Breadcrumb items={[{ label: 'Consultas', to: '/consultations' }, { label: form.isEditMode ? 'Editar Consulta' : 'Nueva Consulta' }]} />
          </div>
          <div className="col-sm-6 text-end">
            {!form.isEditMode && form.lastSavedAt && (
              <span
                className="text-success me-3 small"
                role="status"
                aria-live="polite"
                title="Borrador guardado automáticamente en este navegador"
              >
                <i className="fa fa-check-circle me-1" aria-hidden="true"></i>
                Borrador guardado · {form.lastSavedAt.toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
            )}
            {!form.isEditMode && form.hasUnsavedChanges && !form.lastSavedAt && (
              <span
                className="text-warning me-3 small"
                role="status"
                aria-live="polite"
                title="Cambios pendientes de autoguardado (cada 30 segundos)"
              >
                <i className="fa fa-circle me-1" aria-hidden="true" style={{ fontSize: 8, verticalAlign: 'middle' }}></i>
                Cambios sin guardar
              </span>
            )}
            <Link
              to="/consultations"
              className="btn btn-outline-secondary"
              onClick={(e) => {
                if (!form.isEditMode && form.hasUnsavedChanges) {
                  e.preventDefault();
                  setShowCancelConfirm(true);
                }
              }}
            >
              <i className="fa fa-arrow-left me-1"></i> Volver
            </Link>
          </div>
        </div>
      </div>

      <SectionJumpNav sections={jumpNavSections} />

      {form.isEditMode && form.consultationStatus && form.consultationStatus !== 'draft' && (
        <div className="alert alert-warning">
          <i className="fa fa-lock me-2"></i>
          Esta consulta está firmada y es de solo lectura. Únicamente la modalidad y el enlace de
          videoconsulta pueden guardarse desde aquí; el resto de los campos no se modificará al guardar.
        </div>
      )}

      {loadingAppointment && (
        <div className="alert alert-info">
          <Spinner text="Cargando datos de la cita..." />
        </div>
      )}

      {linkedAppointment && (
        <div className="alert alert-info d-flex align-items-center">
          <i className="fa fa-calendar-check me-2" style={{ fontSize: 18 }}></i>
          <div>
            <strong>Consulta desde cita programada</strong> — {linkedAppointment.appointment_date} {linkedAppointment.appointment_time}
            {linkedAppointment.type && <span className="ms-2 custom-badge status-blue">{linkedAppointment.type.name}</span>}
          </div>
        </div>
      )}

      {form.loadingPreAuths && (
        <div className="alert alert-warning d-flex align-items-center gap-3">
          <i className="fa fa-shield me-1" style={{ fontSize: 18 }} />
          <div className="flex-grow-1">
            <Spinner text="Cargando pre-autorizaciones..." />
          </div>
        </div>
      )}
      {form.activePreAuths.length > 0 && !form.loadingPreAuths && (
        <div className="alert alert-warning d-flex align-items-center gap-3">
          <i className="fa fa-shield me-1" style={{ fontSize: 18 }} />
          <div className="flex-grow-1">
            <strong>Pre-Autorización ARS</strong>
            <select
              className="form-select form-select-sm mt-1"
              value={form.preAuthorizationId ?? ''}
              onChange={(e) => form.setPreAuthorizationId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Sin pre-autorización</option>
              {form.activePreAuths.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.authorization_number} — {p.patientInsurance?.company?.name ?? 'ARS'} — {p.service_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {submitState === 'error' && form.error && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          <span>{form.error}</span>
          <button
            type="button"
            className="btn-close"
            aria-label="Cerrar mensaje de error"
            onClick={dismissSubmitError}
          />
        </div>
      )}
      {submitState === 'submitting' && (
        <div className="alert alert-info d-flex align-items-center" role="status" aria-live="polite">
          <Spinner className="me-2" />
          <span>{form.isEditMode ? 'Actualizando consulta...' : 'Creando consulta...'}</span>
        </div>
      )}
      {submitState === 'success' && (
        <div className="alert alert-success d-flex align-items-center" role="status" aria-live="polite">
          <i className="fa fa-check-circle me-2" aria-hidden="true"></i>
          <span>
            Consulta {form.isEditMode ? 'actualizada' : 'creada'} exitosamente.
            {form.rosSaveWarning && ' No se pudo guardar la revisión por sistemas, intente nuevamente desde el detalle de la consulta.'}
            {' '}Redirigiendo...
          </span>
        </div>
      )}

      <ConfirmModal
        show={!!form.draftRecoveryPrompt && !form.isEditMode}
        title="Recuperar borrador"
        body="Encontramos un borrador guardado de esta consulta. ¿Desea recuperarlo o descartarlo?"
        confirmLabel="Recuperar"
        confirmVariant="primary"
        cancelLabel="Cancelar"
        secondaryAction={{
          label: 'Descartar y empezar de nuevo',
          variant: 'outline-danger',
          onClick: form.dismissDraftRecovery,
        }}
        onConfirm={form.confirmDraftRecovery}
        onCancel={form.dismissDraftRecovery}
      />

      {/* Paciente y Doctor */}
      <div className="row" data-section="paciente-doctor">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title"><SectionNumberBadge number={sectionNumbers['paciente-doctor']} /><i className="fa fa-users me-2"></i>Paciente y Doctor</h4>
              <div className="row mt-3">
                <div className="col-md-6">
                  <div className="mb-3" style={{ position: 'relative' }}>
                    <label className="form-label">Paciente *</label>
                    <input
                      type="text"
                      className={`form-control${fieldErrors.patientId ? ' is-invalid' : ''}`}
                      placeholder="Buscar paciente por nombre o identificación..."
                      value={patientSearch}
                      onChange={(e) => {
                        handlePatientSearch(e.target.value);
                        const error = validateField('patientId', e.target.value ? form.patientId : null);
                        setFieldErrors(prev => {
                          const next = { ...prev };
                          if (error) next.patientId = error;
                          else delete next.patientId;
                          return next;
                        });
                      }}
                      onBlur={() => {
                        const error = validateField('patientId', form.patientId);
                        setFieldErrors(prev => {
                          const next = { ...prev };
                          if (error) next.patientId = error;
                          else delete next.patientId;
                          return next;
                        });
                      }}
                      disabled={!!linkedAppointment}
                      data-field="patientId"
                    />
                    {fieldErrors.patientId && (
                      <div className="invalid-feedback d-block small">
                        {fieldErrors.patientId}
                      </div>
                    )}
                    {form.searchingPatients && (
                      <small className="text-muted">
                        <Spinner text="Buscando..." />
                      </small>
                    )}
                    {form.patients.length > 0 && !form.patientId && !linkedAppointment && (
                      <ul className="list-group" role="listbox" style={{ position: 'absolute', zIndex: 10, width: '100%', maxHeight: 360, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
                        {form.patients.map((p) => {
                          const selectPatient = () => {
                            form.setPatientId(p.id);
                            form.setPatients([]);
                            form.loadActivePreAuths(p.id);
                            setPatientSearch(`${p.first_name} ${p.last_name} - ${p.identification}`);
                            setIsOncologyPatient(p.is_oncology ?? false);
                          };
                          return (
                            <li
                              key={p.id}
                              tabIndex={0}
                              role="option"
                              aria-selected={false}
                              className="list-group-item list-group-item-action py-3"
                              style={{ cursor: 'pointer' }}
                              onClick={selectPatient}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectPatient(); } }}
                            >
                              {p.first_name} {p.last_name} — {p.identification}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    {form.patientId && !linkedAppointment && (
                      <small className="text-success">
                        <i className="fa fa-check me-1"></i> Paciente seleccionado{patientSearch ? `: ${patientSearch}` : ''}{' '}
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 ms-1"
                          onClick={() => {
                            form.setPatientId(null);
                            form.setPatients([]);
                            form.clearLoadedPatientLabel();
                            setPatientSearch('');
                          }}
                        >
                          Cambiar
                        </button>
                      </small>
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Doctor *</label>
                    {form.loadingDoctors ? (
                      <div className="form-control bg-light" style={{ cursor: 'wait' }}>
                        <Spinner text="Cargando doctores..." />
                      </div>
                    ) : isInertAssistantSelector ? (
                      <div className="form-control bg-light text-muted" data-field="doctorId">
                        No tiene doctores asignados. Contacte al administrador.
                      </div>
                    ) : (
                      <select
                        className={`form-control${fieldErrors.doctorId ? ' is-invalid' : ''}`}
                        value={form.doctorId || ''}
                        onChange={(e) => {
                          form.setDoctorId(Number(e.target.value) || null);
                          const error = validateField('doctorId', e.target.value ? Number(e.target.value) : null);
                          setFieldErrors(prev => {
                            const next = { ...prev };
                            if (error) next.doctorId = error;
                            else delete next.doctorId;
                            return next;
                          });
                        }}
                        onBlur={() => {
                          const error = validateField('doctorId', form.doctorId);
                          setFieldErrors(prev => {
                            const next = { ...prev };
                            if (error) next.doctorId = error;
                            else delete next.doctorId;
                            return next;
                          });
                        }}
                        disabled={!!linkedAppointment}
                        data-field="doctorId"
                      >
                        <option value="">Seleccionar doctor...</option>
                        {doctorOptions.map((o) => (
                          <option key={o.id} value={o.id}>{o.label}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Datos de la Consulta */}
      <div className="row" data-section="datos-consulta">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title"><SectionNumberBadge number={sectionNumbers['datos-consulta']} /><i className="fa fa-stethoscope me-2"></i>Datos de la Consulta</h4>
              <div className="row mt-3">
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Modalidad</label>
                    <select
                      className="form-select"
                      value={form.consultationModality}
                      onChange={(e) => form.setConsultationModality(e.target.value)}
                    >
                      <option value="presencial">Presencial</option>
                      <option value="virtual">Virtual</option>
                    </select>
                  </div>
                </div>
                {form.consultationModality === 'virtual' && form.videoCallLink && (
                  <div className="col-md-5">
                    <div className="mb-3">
                      <label className="form-label">Enlace de Videoconsulta</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          value={form.videoCallLink}
                          readOnly
                        />
                        <a
                          href={form.videoCallLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-primary"
                          title="Abrir videoconsulta"
                        >
                          <i className="fa fa-video-camera"></i>
                        </a>
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          title="Copiar enlace"
                          onClick={() => {
                            navigator.clipboard.writeText(form.videoCallLink || '').then(() => {
                              void 0;
                            }).catch(() => {
                              void 0;
                            });
                          }}
                        >
                          <i className="fa fa-copy"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="row mt-3">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Motivo de Consulta *</label>
                    <textarea
                      className={`form-control${fieldErrors.chiefComplaint ? ' is-invalid' : ''}`}
                      rows={3}
                      value={form.chiefComplaint}
                      onChange={(e) => {
                        form.setChiefComplaint(e.target.value);
                        const error = validateField('chiefComplaint', e.target.value);
                        setFieldErrors(prev => {
                          const next = { ...prev };
                          if (error) next.chiefComplaint = error;
                          else delete next.chiefComplaint;
                          return next;
                        });
                      }}
                      onBlur={() => {
                        const error = validateField('chiefComplaint', form.chiefComplaint);
                        setFieldErrors(prev => {
                          const next = { ...prev };
                          if (error) next.chiefComplaint = error;
                          else delete next.chiefComplaint;
                          return next;
                        });
                      }}
                      placeholder="Describa el motivo principal de la consulta..."
                      data-field="chiefComplaint"
                    />
                    {fieldErrors.chiefComplaint && (
                      <div className="invalid-feedback d-block small">
                        {fieldErrors.chiefComplaint}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Enfermedad Actual *</label>
                    <textarea
                      className={`form-control${fieldErrors.currentIllness ? ' is-invalid' : ''}`}
                      rows={3}
                      value={form.currentIllness}
                      onChange={(e) => {
                        form.setCurrentIllness(e.target.value);
                        const error = validateField('currentIllness', e.target.value);
                        setFieldErrors(prev => {
                          const next = { ...prev };
                          if (error) next.currentIllness = error;
                          else delete next.currentIllness;
                          return next;
                        });
                      }}
                      onBlur={() => {
                        const error = validateField('currentIllness', form.currentIllness);
                        setFieldErrors(prev => {
                          const next = { ...prev };
                          if (error) next.currentIllness = error;
                          else delete next.currentIllness;
                          return next;
                        });
                      }}
                      placeholder="Describa la enfermedad actual..."
                      data-field="currentIllness"
                    />
                    {fieldErrors.currentIllness && (
                      <div className="invalid-feedback d-block small">
                        {fieldErrors.currentIllness}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Examen Físico</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={form.physicalExamination}
                      onChange={(e) => form.setPhysicalExamination(e.target.value)}
                      placeholder="Hallazgos del examen físico..."
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Recomendaciones Generales</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={form.generalRecommendations}
                      onChange={(e) => form.setGeneralRecommendations(e.target.value)}
                      placeholder="Recomendaciones para el paciente..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <>

      {/* Signos Vitales */}
      <div className="row" data-section="signos-vitales">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title"><SectionNumberBadge number={sectionNumbers['signos-vitales']} /><i className="fa fa-heartbeat me-2"></i>Signos Vitales</h4>
              <div className="row mt-3">
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">FC (lpm)</label>
                    <input
                      type="number"
                      className={`form-control ${(() => {
                        const s = getVitalRangeStatus('heart_rate', form.vitalSigns.heart_rate);
                        return s.outOfRange ? 'is-invalid' : '';
                      })()}`}
                      value={form.vitalSigns.heart_rate}
                      onChange={(e) => form.updateVitalSign('heart_rate', e.target.value)}
                      placeholder="60-100"
                    />
                    {getVitalRangeStatus('heart_rate', form.vitalSigns.heart_rate).message && (
                      <div className="invalid-feedback">
                        {getVitalRangeStatus('heart_rate', form.vitalSigns.heart_rate).message}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">FR (rpm)</label>
                    <input
                      type="number"
                      className={`form-control ${(() => {
                        const s = getVitalRangeStatus('respiratory_rate', form.vitalSigns.respiratory_rate);
                        return s.outOfRange ? 'is-invalid' : '';
                      })()}`}
                      value={form.vitalSigns.respiratory_rate}
                      onChange={(e) => form.updateVitalSign('respiratory_rate', e.target.value)}
                      placeholder="12-20"
                    />
                    {getVitalRangeStatus('respiratory_rate', form.vitalSigns.respiratory_rate).message && (
                      <div className="invalid-feedback">
                        {getVitalRangeStatus('respiratory_rate', form.vitalSigns.respiratory_rate).message}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">PA Sistólica (mmHg)</label>
                    <input
                      type="number"
                      className={`form-control ${(() => {
                        const s = getVitalRangeStatus('systolic_pressure', form.vitalSigns.systolic_pressure);
                        return s.outOfRange ? 'is-invalid' : '';
                      })()}`}
                      value={form.vitalSigns.systolic_pressure}
                      onChange={(e) => form.updateVitalSign('systolic_pressure', e.target.value)}
                      placeholder="120"
                    />
                    {getVitalRangeStatus('systolic_pressure', form.vitalSigns.systolic_pressure).message && (
                      <div className="invalid-feedback">
                        {getVitalRangeStatus('systolic_pressure', form.vitalSigns.systolic_pressure).message}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">PA Diastólica (mmHg)</label>
                    <input
                      type="number"
                      className={`form-control ${(() => {
                        const s = getVitalRangeStatus('diastolic_pressure', form.vitalSigns.diastolic_pressure);
                        return s.outOfRange ? 'is-invalid' : '';
                      })()}`}
                      value={form.vitalSigns.diastolic_pressure}
                      onChange={(e) => form.updateVitalSign('diastolic_pressure', e.target.value)}
                      placeholder="80"
                    />
                    {getVitalRangeStatus('diastolic_pressure', form.vitalSigns.diastolic_pressure).message && (
                      <div className="invalid-feedback">
                        {getVitalRangeStatus('diastolic_pressure', form.vitalSigns.diastolic_pressure).message}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Temperatura (°C)</label>
                    <input
                      type="number"
                      step="0.1"
                      className={`form-control ${(() => {
                        const s = getVitalRangeStatus('temperature', form.vitalSigns.temperature);
                        return s.outOfRange ? 'is-invalid' : '';
                      })()}`}
                      value={form.vitalSigns.temperature}
                      onChange={(e) => form.updateVitalSign('temperature', e.target.value)}
                      placeholder="36.5"
                    />
                    {getVitalRangeStatus('temperature', form.vitalSigns.temperature).message && (
                      <div className="invalid-feedback">
                        {getVitalRangeStatus('temperature', form.vitalSigns.temperature).message}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Peso (kg)</label>
                    <input
                      type="number"
                      step="0.1"
                      className={`form-control ${(() => {
                        const s = getVitalRangeStatus('weight', form.vitalSigns.weight);
                        return s.outOfRange ? 'is-invalid' : '';
                      })()}`}
                      value={form.vitalSigns.weight}
                      onChange={(e) => form.updateVitalSign('weight', e.target.value)}
                      placeholder="70.0"
                    />
                    {getVitalRangeStatus('weight', form.vitalSigns.weight).message && (
                      <div className="invalid-feedback">
                        {getVitalRangeStatus('weight', form.vitalSigns.weight).message}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Talla (cm)</label>
                    <input
                      type="number"
                      step="0.1"
                      className={`form-control ${(() => {
                        const s = getVitalRangeStatus('height', form.vitalSigns.height);
                        return s.outOfRange ? 'is-invalid' : '';
                      })()}`}
                      value={form.vitalSigns.height}
                      onChange={(e) => form.updateVitalSign('height', e.target.value)}
                      placeholder="170"
                    />
                    {getVitalRangeStatus('height', form.vitalSigns.height).message && (
                      <div className="invalid-feedback">
                        {getVitalRangeStatus('height', form.vitalSigns.height).message}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">SpO2 (%)</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      className={`form-control ${(() => {
                        const s = getVitalRangeStatus('oxygen_saturation', form.vitalSigns.oxygen_saturation);
                        return s.outOfRange ? 'is-invalid' : '';
                      })()}`}
                      value={form.vitalSigns.oxygen_saturation}
                      onChange={(e) => form.updateVitalSign('oxygen_saturation', e.target.value)}
                      placeholder="98"
                    />
                    {getVitalRangeStatus('oxygen_saturation', form.vitalSigns.oxygen_saturation).message && (
                      <div className="invalid-feedback">
                        {getVitalRangeStatus('oxygen_saturation', form.vitalSigns.oxygen_saturation).message}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {(() => {
                const w = parseFloat(form.vitalSigns.weight);
                const h = parseFloat(form.vitalSigns.height);
                // Guard against zero/empty/missing values — without this the badge can
                // render a stale category from a previous computation. The key forces
                // a fresh render whenever the inputs change so React cannot reuse the
                // previous category badge.
                if (!w || !h || w <= 0 || h <= 0) {
                  return (
                    <div
                      key="bmi-empty"
                      className="alert"
                      style={{ background: '#f0f9f0', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 14px', display: 'inline-block' }}
                    >
                      <strong>IMC: <span style={{ color: '#6b7280' }}>—</span></strong>
                      <span className="text-muted ms-2">(requiere peso y talla)</span>
                    </div>
                  );
                }
                const bmi = (w / Math.pow(h / 100, 2)).toFixed(1);
                const bmiNum = parseFloat(bmi);
                const category = bmiNum < 18.5 ? 'Bajo peso' : bmiNum < 25 ? 'Normal' : bmiNum < 30 ? 'Sobrepeso' : 'Obesidad';
                const color = bmiNum < 18.5 ? '#f59e0b' : bmiNum < 25 ? '#10b981' : bmiNum < 30 ? '#f59e0b' : '#ef4444';
                return (
                  <div
                    key={`bmi-${w}-${h}`}
                    className="alert"
                    style={{ background: '#f0f9f0', border: `1px solid ${color}`, borderRadius: 8, padding: '8px 14px', display: 'inline-block' }}
                  >
                    <strong>IMC: <span style={{ color }}>{bmi}</span></strong>
                    <span className="text-muted ms-2">({category})</span>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Evaluación Pediátrica */}
      {isPediatricSpecialty && (
        <PediatricSection
          pediatricData={form.pediatricData}
          setPediatricField={form.setPediatricField}
          patientBirthDate={form.patientBirthDate}
          patientGender={form.patientGender}
          sectionNumber={sectionNumbers['pediatrica']}
        />
      )}

      {/* Evaluación Medicina Interna */}
      {isInternalMedicineSpecialty && (
        <InternalMedicineSection
          internalMedicineData={form.internalMedicineData}
          setInternalMedicineField={form.setInternalMedicineField}
          patientSex={patientSex}
          patientAge={patientAge}
          sectionNumber={sectionNumbers['medicina-interna']}
        />
      )}

      {/* Evaluación Traumatológica */}
      {isTraumatologiaSpecialty && (
        <TraumatologiaSection
          traumatologiaData={form.traumatologiaData}
          setTraumatologiaField={form.setTraumatologiaField}
          sectionNumber={sectionNumbers['traumatologica']}
        />
      )}

      {/* Evaluación Dermatológica */}
      {isDermatologiaSpecialty && (
        <DermatologiaSection
          dermatologiaData={form.dermatologiaData}
          setDermatologiaField={form.setDermatologiaField}
          sectionNumber={sectionNumbers['dermatologica']}
        />
      )}

      {/* Evaluación Oftalmológica */}
      {isOftalmologiaSpecialty && (
        <OftalmologiaSection
          oftalmologiaData={form.oftalmologiaData}
          setOftalmologiaField={form.setOftalmologiaField}
          sectionNumber={sectionNumbers['oftalmologica']}
        />
      )}

      {/* Diagnósticos */}
      <div className="row" data-section="diagnosticos">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title"><SectionNumberBadge number={sectionNumbers['diagnosticos']} /><i className="fa fa-medkit me-2"></i>Diagnósticos (CIE-10)</h4>
              <div className="row mt-3 mb-3">
                <div className="col-md-6" style={{ position: 'relative' }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Buscar diagnóstico por código o descripción..."
                    value={diagnosisSearch}
                    onChange={(e) => handleDiagnosisSearch(e.target.value)}
                  />
                  {form.searchingDiagnoses && (
                    <small className="text-muted">
                      <Spinner text="Buscando..." />
                    </small>
                  )}
                  {form.diagnosisResults.length > 0 && (
                    <ul className="list-group" role="listbox" style={{ position: 'absolute', zIndex: 10, width: '95%', maxHeight: 200, overflowY: 'auto' }}>
                      {form.diagnosisResults.map((d) => (
                        <li
                          key={d.id}
                          tabIndex={0}
                          role="option"
                          aria-selected={false}
                          className="list-group-item list-group-item-action"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleAddDiagnosis(d)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleAddDiagnosis(d); } }}
                        >
                          <strong>{d.code}</strong> — {d.description}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="col-md-3">
                  <select
                    className="form-control"
                    value={diagnosisType}
                    onChange={(e) => setDiagnosisType(e.target.value)}
                  >
                    <option value="principal">Principal</option>
                    <option value="secundario">Secundario</option>
                    <option value="presuntivo">Presuntivo</option>
                  </select>
                </div>
              </div>

              {form.diagnoses.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Descripción</th>
                        <th>Tipo</th>
                        <th>Observaciones</th>
                        <th style={{ width: 50 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.diagnoses.map((d, i) => (
                        <tr key={i}>
                          <td><strong>{d.diagnosis?.code || '-'}</strong></td>
                          <td>{d.diagnosis?.description || '-'}</td>
                          <td>
                            <span className={`custom-badge ${d.diagnosis_type === 'principal' ? 'status-blue' : d.diagnosis_type === 'secundario' ? 'status-purple' : 'status-orange'}`}>
                              {d.diagnosis_type}
                            </span>
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={d.observations}
                              onChange={(e) => form.updateDiagnosisObservation(i, e.target.value)}
                              placeholder="Observaciones..."
                            />
                          </td>
                          <td>
                            <button
                              className="btn btn-sm bg-danger-light"
                              onClick={() => form.removeDiagnosis(i)}
                            >
                              <i className="fa fa-times"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recetas */}
      <div className="row" data-section="recetas">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="card-title mb-0"><SectionNumberBadge number={sectionNumbers['recetas']} /><i className="fa fa-file-lines me-2"></i>Recetas</h4>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => form.addPrescription()}
                >
                  <i className="fa fa-plus me-1"></i> Agregar Medicamento
                </button>
              </div>

              {form.prescriptions.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Medicamento</th>
                        <th>Dosis</th>
                        <th>Frecuencia</th>
                        <th>Duración</th>
                        <th>Instrucciones</th>
                        <th style={{ width: 50 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.prescriptions.map((p, i) => (
                        <tr key={i}>
                          <td style={{ minWidth: 200 }}>
                            <MedicationSelect
                              value={p.medication_id}
                              onSelect={(id, name) => { form.updatePrescription(i, 'medication_id', id); form.updatePrescription(i, 'medication_name', name); }}
                              onClear={() => { form.updatePrescription(i, 'medication_id', null); form.updatePrescription(i, 'medication_name', ''); }}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={p.dosage}
                              onChange={(e) => form.updatePrescription(i, 'dosage', e.target.value)}
                              placeholder="Ej: 500mg"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={p.frequency}
                              onChange={(e) => form.updatePrescription(i, 'frequency', e.target.value)}
                              placeholder="Ej: Cada 8 horas"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={p.duration}
                              onChange={(e) => form.updatePrescription(i, 'duration', e.target.value)}
                              placeholder="Ej: 7 días"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={p.instructions}
                              onChange={(e) => form.updatePrescription(i, 'instructions', e.target.value)}
                              placeholder="Instrucciones adicionales"
                            />
                          </td>
                          <td>
                            <button
                              className="btn btn-sm bg-danger-light"
                              onClick={() => form.removePrescription(i)}
                            >
                              <i className="fa fa-times"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Órdenes de Laboratorio */}
      <div className="row" data-section="lab">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="card-title mb-0"><SectionNumberBadge number={sectionNumbers['lab']} /><i className="fa fa-flask me-2"></i>Órdenes de Laboratorio</h4>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => form.setLabOrders([...form.labOrders, { test_id: null, test_name: '', panel_id: null, urgency: 'routine', instructions: '' }])}
                >
                  <i className="fa fa-plus me-1"></i> Agregar Orden
                </button>
              </div>

              {form.labOrders.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Prueba / Panel</th>
                        <th>Urgencia</th>
                        <th>Instrucciones</th>
                        <th style={{ width: 50 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.labOrders.map((order, i) => (
                        <tr key={i}>
                          <td>
                            <LabTestSelect
                              value={order.test_id}
                              panelId={order.panel_id}
                              onSelectTest={(testId, testName) => {
                                form.setLabOrders(prev => prev.map((o, idx) =>
                                  idx === i ? { ...o, test_id: testId, panel_id: null, test_name: testName } : o
                                ));
                              }}
                              onSelectPanel={(panelId, panelName) => {
                                form.setLabOrders(prev => prev.map((o, idx) =>
                                  idx === i ? { ...o, panel_id: panelId, test_id: null, test_name: panelName } : o
                                ));
                              }}
                              onClear={() => {
                                form.setLabOrders(prev => prev.map((o, idx) =>
                                  idx === i ? { ...o, test_id: null, panel_id: null, test_name: '' } : o
                                ));
                              }}
                            />
                          </td>
                          <td>
                            <select
                              className="form-control form-control-sm"
                              value={order.urgency}
                              onChange={(e) => {
                                const updated = [...form.labOrders];
                                updated[i] = { ...updated[i], urgency: e.target.value as 'routine' | 'urgent' | 'stat' };
                                form.setLabOrders(updated);
                              }}
                            >
                              <option value="routine">Rutina</option>
                              <option value="urgent">Urgente</option>
                              <option value="stat">STAT</option>
                            </select>
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={order.instructions}
                              onChange={(e) => {
                                const updated = [...form.labOrders];
                                updated[i] = { ...updated[i], instructions: e.target.value };
                                form.setLabOrders(updated);
                              }}
                              placeholder="Instrucciones especiales"
                            />
                          </td>
                          <td>
                            <button
                              className="btn btn-sm bg-danger-light"
                              onClick={() => form.setLabOrders(form.labOrders.filter((_, idx) => idx !== i))}
                            >
                              <i className="fa fa-times"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Órdenes de Imágenes */}
      <div className="row" data-section="imagenes">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="card-title mb-0"><SectionNumberBadge number={sectionNumbers['imagenes']} /><i className="fa fa-image me-2"></i>Órdenes de Imágenes</h4>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => form.setImagingOrders([...form.imagingOrders, { imaging_study_id: 0, imaging_study_name: '', clinical_indication: '', priority: 'routine' }])}
                >
                  <i className="fa fa-plus me-1"></i> Agregar Orden
                </button>
              </div>

              {form.imagingOrders.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Estudio</th>
                        <th>Indicación Clínica</th>
                        <th>Prioridad</th>
                        <th style={{ width: 50 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.imagingOrders.map((order, i) => (
                        <tr key={i}>
                          <td>
                            <ImagingStudySelect
                              value={order.imaging_study_id || null}
                              onSelect={(id, name) => {
                                form.setImagingOrders(prev => prev.map((o, idx) =>
                                  idx === i ? { ...o, imaging_study_id: id, imaging_study_name: name } : o
                                ));
                              }}
                              onClear={() => {
                                form.setImagingOrders(prev => prev.map((o, idx) =>
                                  idx === i ? { ...o, imaging_study_id: 0, imaging_study_name: '' } : o
                                ));
                              }}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={order.clinical_indication}
                              onChange={(e) => {
                                const updated = [...form.imagingOrders];
                                updated[i] = { ...updated[i], clinical_indication: e.target.value };
                                form.setImagingOrders(updated);
                              }}
                              placeholder="Razón del estudio"
                            />
                          </td>
                          <td>
                            <select
                              className="form-control form-control-sm"
                              value={order.priority}
                              onChange={(e) => {
                                const updated = [...form.imagingOrders];
                                updated[i] = { ...updated[i], priority: e.target.value as 'routine' | 'urgent' | 'stat' };
                                form.setImagingOrders(updated);
                              }}
                            >
                              <option value="routine">Rutina</option>
                              <option value="urgent">Urgente</option>
                              <option value="stat">STAT</option>
                            </select>
                          </td>
                          <td>
                            <button
                              className="btn btn-sm bg-danger-light"
                              onClick={() => form.setImagingOrders(form.imagingOrders.filter((_, idx) => idx !== i))}
                            >
                              <i className="fa fa-times"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      </>

      {/* Información Adicional */}
      <div className="row" data-section="info-adicional">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title"><SectionNumberBadge number={sectionNumbers['info-adicional']} /><i className="fa fa-info-circle me-2"></i>Información Adicional</h4>
              <div className="row mt-3">
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Acompañante</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.companionName}
                      onChange={(e) => form.setCompanionName(e.target.value)}
                      placeholder="Nombre del acompañante"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Parentesco</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.companionRelationship}
                      onChange={(e) => form.setCompanionRelationship(e.target.value)}
                      placeholder="Ej: Madre, Esposo..."
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Fecha de Control</label>
                    <input
                      type="date"
                      className="form-control"
                      value={form.followUpDate}
                      onChange={(e) => form.setFollowUpDate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-2">
                  {isModuleEnabled('admissions') && (
                  <div className="mb-3">
                    <label className="form-label">¿Requiere Admisión?</label>
                    <div className="form-check mt-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={form.requiresAdmission}
                        onChange={(e) => form.setRequiresAdmission(e.target.checked)}
                        id="requiresAdmission"
                      />
                      <label className="form-check-label" htmlFor="requiresAdmission">Sí</label>
                    </div>
                  </div>
                  )}
                </div>
                {form.requiresAdmission && (
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">Motivo de Admisión *</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={form.admissionReason}
                        onChange={(e) => form.setAdmissionReason(e.target.value)}
                        placeholder="Razón por la que se requiere admisión..."
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cardiology Section */}
      {isCardiologiaSpecialty && (
        <div className="row mb-4" data-section="cardiologica">
          <div className="col-sm-12">
            <div className="card border-danger">
              <div className="card-header bg-danger text-white">
                <h4 className="card-title mb-0">
                  <SectionNumberBadge number={sectionNumbers['cardiologica']} inverse />
                  <i className="fa fa-heartbeat me-2"></i>
                  Evaluación Cardiológica
                </h4>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">Clase Funcional NYHA</label>
                      <select
                        className="form-select"
                        value={form.specialtyData.nyha_class || ''}
                        onChange={(e) => form.setSpecialtyData(prev => ({ ...prev, nyha_class: e.target.value }))}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="I">Clase I — Sin limitación</option>
                        <option value="II">Clase II — Limitación leve</option>
                        <option value="III">Clase III — Limitación marcada</option>
                        <option value="IV">Clase IV — Síntomas en reposo</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-8">
                    <div className="mb-3">
                      <label className="form-label">Examen Cardiovascular</label>
                      <input
                        type="text"
                        className="form-control"
                        value={form.specialtyData.cardiac_exam || ''}
                        onChange={(e) => form.setSpecialtyData(prev => ({ ...prev, cardiac_exam: e.target.value }))}
                        placeholder="Ruidos cardíacos, soplos, ritmo, pulsos periféricos..."
                      />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Factores de Riesgo Cardiovascular</label>
                      <div className="d-flex flex-wrap gap-3">
                        {['HTA', 'Diabetes', 'Tabaquismo', 'Dislipidemia', 'Obesidad', 'Antecedente familiar'].map(factor => (
                          <div className="form-check" key={factor}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={`rf-${factor}`}
                              checked={(form.specialtyData.risk_factors || '').split(',').map(s => s.trim()).includes(factor)}
                              onChange={(e) => {
                                const current = (form.specialtyData.risk_factors || '').split(',').map(s => s.trim()).filter(Boolean);
                                const updated = e.target.checked
                                  ? [...current, factor]
                                  : current.filter(f => f !== factor);
                                form.setSpecialtyData(prev => ({ ...prev, risk_factors: updated.join(', ') }));
                              }}
                            />
                            <label className="form-check-label" htmlFor={`rf-${factor}`}>{factor}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Hallazgos ECG</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={form.specialtyData.ecg_findings || ''}
                        onChange={(e) => form.setSpecialtyData(prev => ({ ...prev, ecg_findings: e.target.value }))}
                        placeholder="Ritmo sinusal, taquicardia, bloqueos, cambios ST-T..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gynecology Section */}
      {isGinecologiaSpecialty && (
        <div className="row mb-4" data-section="ginecologica">
          <div className="col-sm-12">
            <div className="card border-primary">
              <div className="card-header text-white" style={{ backgroundColor: '#7b2d8b' }}>
                <h4 className="card-title mb-0">
                  <SectionNumberBadge number={sectionNumbers['ginecologica']} inverse />
                  <i className="fa fa-venus me-2"></i>
                  Evaluación Ginecológica
                </h4>
              </div>
              <div className="card-body">
                <div className="row">
                  {/* Antecedentes obstétricos */}
                  <div className="col-12 mb-2">
                    <h6 className="text-muted fw-semibold">Antecedentes Obstétricos</h6>
                  </div>
                  <div className="col-md-2">
                    <div className="mb-3">
                      <label className="form-label">Gestas (G)</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        placeholder="0"
                        value={form.specialtyData.g_gestas || ''}
                        onChange={(e) => form.setSpecialtyData(prev => ({ ...prev, g_gestas: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="mb-3">
                      <label className="form-label">Partos (P)</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        placeholder="0"
                        value={form.specialtyData.p_partos || ''}
                        onChange={(e) => form.setSpecialtyData(prev => ({ ...prev, p_partos: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="mb-3">
                      <label className="form-label">Abortos (A)</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        placeholder="0"
                        value={form.specialtyData.a_abortos || ''}
                        onChange={(e) => form.setSpecialtyData(prev => ({ ...prev, a_abortos: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="mb-3">
                      <label className="form-label">Cesáreas (C)</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        placeholder="0"
                        value={form.specialtyData.c_cesareas || ''}
                        onChange={(e) => form.setSpecialtyData(prev => ({ ...prev, c_cesareas: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="mb-3">
                      <label className="form-label">FUM</label>
                      <input
                        type="date"
                        className="form-control"
                        value={form.specialtyData.fum || ''}
                        onChange={(e) => form.setSpecialtyData(prev => ({ ...prev, fum: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="mb-3">
                      <label className="form-label">FPP</label>
                      <input
                        type="date"
                        className="form-control"
                        value={form.specialtyData.fpp || ''}
                        onChange={(e) => form.setSpecialtyData(prev => ({ ...prev, fpp: e.target.value }))}
                      />
                    </div>
                  </div>

                  {/* Antecedentes ginecológicos */}
                  <div className="col-12 mb-2 mt-1">
                    <h6 className="text-muted fw-semibold">Antecedentes Ginecológicos</h6>
                  </div>
                  <div className="col-md-3">
                    <div className="mb-3">
                      <label className="form-label">Menarquia (edad)</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        max={25}
                        placeholder="Ej: 12"
                        value={form.specialtyData.menarquia || ''}
                        onChange={(e) => form.setSpecialtyData(prev => ({ ...prev, menarquia: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="mb-3">
                      <label className="form-label">Ciclo Menstrual</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Ej: regular, 28/5"
                        value={form.specialtyData.ciclo_menstrual || ''}
                        onChange={(e) => form.setSpecialtyData(prev => ({ ...prev, ciclo_menstrual: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="mb-3">
                      <label className="form-label">Último Papanicolaou</label>
                      <input
                        type="date"
                        className="form-control"
                        value={form.specialtyData.ultimo_pap || ''}
                        onChange={(e) => form.setSpecialtyData(prev => ({ ...prev, ultimo_pap: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="mb-3">
                      <label className="form-label">Método Anticonceptivo</label>
                      <select
                        className="form-select"
                        value={form.specialtyData.metodo_anticonceptivo || ''}
                        onChange={(e) => form.setSpecialtyData(prev => ({ ...prev, metodo_anticonceptivo: e.target.value }))}
                      >
                        <option value="">Ninguno / No especificado</option>
                        <option value="Oral">Anticonceptivo oral</option>
                        <option value="DIU">DIU</option>
                        <option value="Implante">Implante</option>
                        <option value="Inyectable">Inyectable</option>
                        <option value="Condón">Condón</option>
                        <option value="Ligadura">Ligadura de trompas</option>
                        <option value="Otro">Otro</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Hallazgos Ginecológicos</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        placeholder="Examen pélvico, especuloscopía, tacto vaginal, hallazgos relevantes..."
                        value={form.specialtyData.hallazgos_ginecologicos || ''}
                        onChange={(e) => form.setSpecialtyData(prev => ({ ...prev, hallazgos_ginecologicos: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Surgery / Pre-op Section */}
      {isCirugiaSpecialty && (
        <div className="row mb-4" data-section="quirurgica">
          <div className="col-sm-12">
            <div className="card border-warning">
              <div className="card-header text-white" style={{ backgroundColor: '#e67e22' }}>
                <h4 className="card-title mb-0">
                  <SectionNumberBadge number={sectionNumbers['quirurgica']} inverse />
                  <i className="fa fa-clipboard-list me-2"></i>
                  Evaluación Quirúrgica
                </h4>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">Clasificación ASA</label>
                      <select
                        className="form-select"
                        value={form.specialtyData.asa_class || ''}
                        onChange={(e) => form.setSpecialtyData(prev => ({ ...prev, asa_class: e.target.value }))}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="I">ASA I — Paciente sano</option>
                        <option value="II">ASA II — Enfermedad sistémica leve</option>
                        <option value="III">ASA III — Enfermedad sistémica grave</option>
                        <option value="IV">ASA IV — Riesgo vital constante</option>
                        <option value="V">ASA V — Moribundo</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-8">
                    <div className="mb-3">
                      <label className="form-label">Indicación Quirúrgica</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Motivo que justifica la intervención quirúrgica..."
                        value={form.specialtyData.indicacion_quirurgica || ''}
                        onChange={(e) => form.setSpecialtyData(prev => ({ ...prev, indicacion_quirurgica: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Factores de Riesgo Quirúrgico</label>
                      <div className="d-flex flex-wrap gap-3">
                        {['HTA', 'Diabetes', 'Anticoagulantes', 'Obesidad', 'Tabaquismo', 'Cardiopatía', 'Coagulopatía'].map(factor => (
                          <div className="form-check" key={factor}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={`sf-${factor}`}
                              checked={(form.specialtyData.surgical_risk_factors || '').split(',').map((s: string) => s.trim()).includes(factor)}
                              onChange={(e) => {
                                const current = (form.specialtyData.surgical_risk_factors || '').split(',').map((s: string) => s.trim()).filter(Boolean);
                                const updated = e.target.checked
                                  ? [...current, factor]
                                  : current.filter((f: string) => f !== factor);
                                form.setSpecialtyData(prev => ({ ...prev, surgical_risk_factors: updated.join(', ') }));
                              }}
                            />
                            <label className="form-check-label" htmlFor={`sf-${factor}`}>{factor}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Notas Preoperatorias</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        placeholder="Exámenes preoperatorios solicitados, preparación, interconsultas..."
                        value={form.specialtyData.preop_notes || ''}
                        onChange={(e) => form.setSpecialtyData(prev => ({ ...prev, preop_notes: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SOAP - Nota Estructurada */}
      <div className="row" data-section="soap">
        <div className="col-sm-12">
          <SOAPFields
            subjective={form.soapFields?.subjective ?? ''}
            objective={form.soapFields?.objective ?? ''}
            assessment={form.soapFields?.assessment ?? ''}
            plan={form.soapFields?.plan ?? ''}
            onChange={(field, value) => {
              form.setSoapFields(prev => ({ ...prev, [field]: value }));
            }}
            sectionNumber={sectionNumbers['soap']}
          />
        </div>
      </div>

      {/* Review of Systems */}
      <div className="row" data-section="ros">
        <div className="col-sm-12">
          <ReviewOfSystemsChecklist
            systems={form.reviewOfSystems}
            onChange={(systems) => form.setReviewOfSystems(systems)}
            sectionNumber={sectionNumbers['ros']}
          />
        </div>
      </div>

      {/* TNM Oncology Staging - only available in edit mode when consultation exists */}
      {showTnmSection && editId && (
        <div className="row mt-3" data-section="tnm">
          <div className="col-sm-12">
            {!showTnmStaging && !tnmStaging && (
              <button
                type="button"
                className="btn btn-outline-primary mb-3"
                onClick={() => setShowTnmStaging(true)}
              >
                <i className="fa fa-heartbeat me-2"></i>
                Agregar Estadificación TNM (Oncología)
              </button>
            )}
            {showTnmStaging && (
              <>
                {loadingTnmStaging ? (
                  <Spinner variant="full-page" text="Cargando información de estadificación..." />
                ) : (
                  <TnmStagingForm
                    consultationId={editId}
                    existingStaging={tnmStaging}
                    onSave={handleSaveTnmStaging}
                    onDelete={handleDeleteTnmStaging}
                    sectionNumber={sectionNumbers['tnm']}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Submit */}
      <div className="row mb-4">
        <div className="col-sm-12 text-end">
          <Link
            to="/consultations"
            onClick={handleCancelClick}
            className="btn btn-outline-secondary me-2"
          >
            Cancelar
          </Link>
          {!form.isEditMode && form.hasUnsavedChanges && (
            <span className="text-warning me-2">
              <i className="fa fa-exclamation-circle"></i> Borrador guardado
            </span>
          )}
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={submitState === 'submitting' || submitState === 'success'}
          >
            {submitState === 'submitting' ? (
              <Spinner size="sm" text="Guardando..." />
            ) : submitState === 'success' ? (
              <><i className="fa fa-check me-1" aria-hidden="true"></i> Guardado</>
            ) : (
              <><i className="fa fa-save me-1" aria-hidden="true"></i> {form.isEditMode ? 'Guardar Cambios' : 'Guardar Consulta'}</>
            )}
          </button>
          {canSign && (
            <button
              type="button"
              className="btn btn-success ms-2"
              disabled={signing}
              onClick={() => sign()}
              title="Firmar consulta"
            >
              <i className="fa fa-signature me-1"></i> {signing ? 'Firmando...' : 'Firmar consulta'}
            </button>
          )}
          {signError && <div className="text-danger mt-2">{signError}</div>}
        </div>
      </div>

      <ConfirmModal
        show={showCancelConfirm}
        title="¿Descartar cambios?"
        body="Tiene un borrador sin guardar. Si sale ahora, perderá los cambios. ¿Desea descartar el borrador y volver al listado?"
        confirmLabel="Descartar y salir"
        cancelLabel="Seguir editando"
        confirmVariant="danger"
        onConfirm={handleConfirmDiscard}
        onCancel={() => setShowCancelConfirm(false)}
      />
    </>
  );
};

const ConsultationFormPageBoundary: React.FC = () => (
  <ErrorBoundary fallbackTitle="No se pudo cargar la consulta">
    <ConsultationFormPage />
  </ErrorBoundary>
);

export default ConsultationFormPageBoundary;
